import { useState, useEffect, useCallback } from 'react';

export interface UploadJob {
  id: string; // announcement_id
  fileName: string;
  uploadedAt: string;
  status: 'uploading' | 'processing' | 'completed' | 'failed';
}

const STORAGE_KEY = 'assayer_upload_queue';

/**
 * Hook to manage upload queue with localStorage persistence
 * Allows tracking multiple concurrent uploads across page reloads
 */
export function useUploadQueue() {
  const [jobs, setJobs] = useState<UploadJob[]>(() => {
    // Initialize from localStorage
    // Note: We keep ALL jobs from localStorage and let the backend's processing
    // status be the source of truth. UploadProgressCard will poll the backend
    // and update status naturally based on the actual processing state.
    // Jobs are only filtered out when user explicitly closes them or uses
    // "Clear Completed" button.
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const now = Date.now();
        const oneDayAgo = now - 24 * 60 * 60 * 1000;
        const fiveMinutesAgo = now - 5 * 60 * 1000;

        return parsed.map((job: UploadJob) => {
          const jobTime = new Date(job.uploadedAt).getTime();

          // Mark stale temp uploads as failed — these are uploads that never
          // completed (timeout, navigation, network loss). Temp IDs start with
          // "temp-" and mean the HTTP request hasn't returned yet.
          if (job.status === 'uploading' && job.id.startsWith('temp-') && jobTime < fiveMinutesAgo) {
            return { ...job, status: 'failed' as const };
          }

          return job;
        }).filter((job: UploadJob) => {
          const jobTime = new Date(job.uploadedAt).getTime();

          // Filter out old completed/failed jobs (>24 hours)
          if (job.status === 'completed' || job.status === 'failed') {
            return jobTime > oneDayAgo;
          }

          // Keep all processing jobs - backend status is source of truth
          return true;
        });
      }
    } catch (err) {
      console.error('Failed to load upload queue from localStorage:', err);
    }
    return [];
  });

  // Persist to localStorage whenever jobs change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
    } catch (err) {
      console.error('Failed to save upload queue to localStorage:', err);
    }
  }, [jobs]);

  const addJob = useCallback((job: UploadJob) => {
    setJobs((prev) => [job, ...prev]); // Add to beginning
  }, []);

  const updateJob = useCallback((id: string, updates: Partial<UploadJob>) => {
    setJobs((prev) =>
      prev.map((job) =>
        job.id === id ? { ...job, ...updates } : job
      )
    );
  }, []);

  const removeJob = useCallback((id: string) => {
    setJobs((prev) => prev.filter((job) => job.id !== id));
  }, []);

  const clearCompleted = useCallback(() => {
    setJobs((prev) =>
      prev.filter((job) => job.status !== 'completed' && job.status !== 'failed')
    );
  }, []);

  const clearAll = useCallback(() => {
    setJobs([]);
  }, []);

  const getJobById = useCallback(
    (id: string) => jobs.find((job) => job.id === id),
    [jobs]
  );

  // Get active (non-completed/failed) jobs
  const activeJobs = jobs.filter(
    (job) => job.status === 'uploading' || job.status === 'processing'
  );

  // Get completed jobs
  const completedJobs = jobs.filter(
    (job) => job.status === 'completed' || job.status === 'failed'
  );

  return {
    jobs,
    activeJobs,
    completedJobs,
    addJob,
    updateJob,
    removeJob,
    clearCompleted,
    clearAll,
    getJobById,
  };
}
